/**
 * MarketDataPanel — the sizer's Market Data card.
 *
 * ONE endpoint: `GET /pricing/market_pulse/{zip}` →
 * `{zipCode, source: 'housecanary'|'zhvi'|null, data, zhvi}`. The server resolves
 * the source — HouseCanary market pulse FIRST (the richer, billed feed, cached
 * 30 days per zip so Refresh is billing-safe), ZHVI as the fallback when HC has
 * nothing for the zip. **The FE never picks the source; it renders what `source`
 * says** (pricing-scenario-sizers.md, shipping requirement 7).
 *
 * That replaced three digital-sizer routes this backend never served
 * (`/integrations/housecanary/market-data`, `/integrations/housecanary/market-trends`,
 * `/integrations/census/rural-check`) — every one of them 404'd, so the panel
 * could only ever show its error state. The rural signal now comes from the
 * pricing response's `marketAssessment` instead of its own lookup.
 *
 * When the RTL scenario has already been priced, its response carries the SAME
 * resolved block as `marketData` — pass it in and the card renders with no
 * second call at all.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { sizerApi } from "../../../api/sizerApi";
import { fmtMoney, fmtPct, humanize } from "../sizerFormat";
import {
  SZ,
  ResultsCard,
  StatusPill,
  EmptyState,
  Kv,
  Badge,
} from "../../sizersShared/sizerUi";
import { ResultGroup, Callout } from "../../sizersShared/sizerResultUi";

/**
 * The HC pulse is stored as the raw vendor payload, whose envelope shape is the
 * vendor's to change. Rather than hardcode a path into it, find the first object
 * carrying the listing-stats keys — the same tolerant strategy the backend's own
 * `_hc_history_points` uses on the history side.
 */
function findListingStats(node, depth = 0) {
  if (!node || depth > 6) return null;
  if (Array.isArray(node)) {
    for (const v of node) {
      const hit = findListingStats(v, depth + 1);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof node !== "object") return null;
  if ("daysOnMarketMedian" in node || "listingsOnMarketCount" in node) return node;
  for (const v of Object.values(node)) {
    const hit = findListingStats(v, depth + 1);
    if (hit) return hit;
  }
  return null;
}

/** Sparkline over a [{date, value}] series. Presentation only — no derivation. */
function Spark({ series, height = 60 }) {
  if (!series || series.length < 2) return null;
  const W = 320;
  const pad = 4;
  const vals = series.map((s) => s.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const pts = series
    .map((s, i) => {
      const x = pad + (i / (series.length - 1)) * (W - pad * 2);
      const y = height - pad - ((s.value - min) / span) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${W} ${height}`}
      role="img"
      aria-label="Market history"
      style={{ display: "block" }}
    >
      <polyline fill="none" stroke={SZ.teal600} strokeWidth={1.5} points={pts} />
    </svg>
  );
}

/** HouseCanary market pulse: the latest listing stats + the 12-month history. */
function HouseCanaryBody({ data }) {
  const stats = findListingStats(data?.latest) || {};
  const history = data?.history;
  const series = (() => {
    const pts = findHistoryPoints(history) || [];
    return pts
      .map((p) => ({
        date: p.eventDate || p.event_date,
        value: p.priceOnMarketMedian ?? p.listingsOnMarketCount ?? null,
      }))
      .filter((p) => p.value != null);
  })();

  return (
    <>
      <Flx g={3} sx={{ flexWrap: "wrap", mb: 1.5 }}>
        <Metric label="Median list price" value={fmtMoney(stats.priceOnMarketMedian)} />
        <Metric
          label="Days on market"
          value={stats.daysOnMarketMedian == null ? "—" : `${stats.daysOnMarketMedian}d`}
        />
      </Flx>
      <Box>
        <Kv k="Listings on market" v={stats.listingsOnMarketCount ?? "—"} />
        <Kv k="New listings" v={stats.listingsNewCount ?? "—"} />
        <Kv k="Under contract" v={fmtPct(pctOf(stats.listingsUnderContractPercent), 1)} />
        <Kv k="Price drops" v={fmtPct(pctOf(stats.listingsPriceDropPercent), 1)} />
        <Kv k="Months of supply" v={stats.monthsOfSupplyMedian ?? "—"} />
      </Box>
      {series.length > 1 && (
        <ResultGroup label={`History · ${series.length} pts`}>
          <Spark series={series} />
        </ResultGroup>
      )}
    </>
  );
}

// HC reports these as percentages already (0–100) in some feeds and fractions in
// others; normalize to the fraction fmtPct expects without inventing a number.
const pctOf = (v) => (v == null ? null : v > 1 ? v / 100 : v);

function findHistoryPoints(node, depth = 0) {
  if (!node || depth > 6) return null;
  if (Array.isArray(node)) {
    if (node.length && typeof node[0] === "object" && node[0] && ("eventDate" in node[0] || "event_date" in node[0])) {
      return node;
    }
    for (const v of node) {
      const hit = findHistoryPoints(v, depth + 1);
      if (hit) return hit;
    }
    return null;
  }
  if (typeof node !== "object") return null;
  for (const v of Object.values(node)) {
    const hit = findHistoryPoints(v, depth + 1);
    if (hit) return hit;
  }
  return null;
}

/** ZHVI fallback: the canonical per-zip Zillow record. */
function ZhviBody({ data }) {
  const ts = (data?.timeSeries || [])
    .map((p) => ({ date: p.date, value: p.value }))
    .filter((p) => p.value != null);
  return (
    <>
      <Flx g={3} sx={{ flexWrap: "wrap", mb: 1.5 }}>
        <Metric label="Median value (ZHVI)" value={fmtMoney(data?.latestZHVI)} />
        <Metric
          label="HPA (YoY)"
          value={fmtPct(data?.hpaYoY, 2)}
          tone={data?.hpaYoY < 0 ? "neg" : "pos"}
        />
      </Flx>
      <Box>
        <Kv k="Metro" v={data?.metro || data?.metroStatus || "—"} />
        <Kv k="County" v={data?.countyName || "—"} />
        <Kv k="As of" v={data?.lastReportedDate || "—"} />
        {data?.hpaMoM != null && <Kv k="HPA (MoM)" v={fmtPct(data.hpaMoM, 2)} />}
      </Box>
      {ts.length > 1 && (
        <ResultGroup label={`ZHVI history · ${ts.length} pts`}>
          <Spark series={ts.slice(-60)} />
        </ResultGroup>
      )}
    </>
  );
}

const Metric = ({ label, value, tone }) => (
  <Box>
    <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>{label}</Txt>
    <Txt
      sx={{
        fontFamily: SZ.mono,
        fontSize: 18,
        fontWeight: 600,
        color: tone === "neg" ? SZ.bad : tone === "pos" ? SZ.good : SZ.ink900,
      }}
    >
      {value}
    </Txt>
  </Box>
);

/**
 * `marketAssessment` from the pricing response — the guideline-facing read of
 * the same market (ZHVI multiplier, rural signal, trend, and the flags that
 * became leverage reductions). Rendered here because it is about the market, not
 * the price.
 */
function AssessmentBody({ assessment }) {
  if (!assessment || !Object.keys(assessment).length) return null;
  const flags = assessment.guidelineFlags || [];
  return (
    <ResultGroup label="Guideline read">
      <Flx g={1} sx={{ mb: 1, flexWrap: "wrap" }}>
        {assessment.trend && <Badge tone="info">{humanize(assessment.trend)}</Badge>}
        {assessment.ruralSignal && <Badge tone="fail">Rural signal</Badge>}
      </Flx>
      {assessment.zillowMultiplier != null && (
        <Kv k="ZHVI multiplier" v={`${Number(assessment.zillowMultiplier).toFixed(2)}x`} />
      )}
      {assessment.subjectValue != null && (
        <Kv k="Subject value" v={fmtMoney(assessment.subjectValue)} />
      )}
      {assessment.hpa3mAnnualized != null && (
        <Kv k="HPA 3mo (ann.)" v={fmtPct(assessment.hpa3mAnnualized, 2)} />
      )}
      {flags.map((f, i) => (
        <Txt key={i} sx={{ fontSize: 11.5, color: SZ.warn, mt: 0.5 }}>
          • {f.reason}
          {f.reduction != null ? ` (−${fmtPct(f.reduction, 2)})` : ""}
        </Txt>
      ))}
    </ResultGroup>
  );
}

export default function MarketDataPanel({
  zipcode,
  marketData,
  marketAssessment,
  // HC pulls are server-cached 30 days per zip, so an automatic fetch costs at
  // most one billed call per zip per month. Still opt-in: the panel is on a form
  // the user is mid-way through typing.
  autoFetch = false,
  autoFetchDebounceMs = 1500,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(null);
  const debounceRef = useRef(null);

  // A priced scenario already carries the resolved block — prefer it, and fall
  // back to whatever this panel fetched itself.
  const resolved = marketData || fetched;
  const source = resolved?.source || null;

  const load = useCallback(async (zip) => {
    if (!zip) return;
    setBusy(true);
    setError(null);
    try {
      setFetched(await sizerApi.marketPulse(zip));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load market data");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!autoFetch || !zipcode || marketData) return undefined;
    debounceRef.current = window.setTimeout(() => load(zipcode), autoFetchDebounceMs);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [zipcode, marketData, autoFetch, autoFetchDebounceMs, load]);

  const title =
    source === "housecanary"
      ? "Market Data — HouseCanary"
      : source === "zhvi"
        ? "Market Data — ZHVI"
        : "Market Data";

  const pill = busy ? (
    <StatusPill state="est">Loading…</StatusPill>
  ) : source ? (
    <StatusPill state="live">{zipcode || resolved?.zipCode || "—"}</StatusPill>
  ) : (
    <StatusPill state="idle">No data</StatusPill>
  );

  let body;
  if (busy && !resolved) {
    body = (
      <Flx center g={1.5} sx={{ py: 3 }}>
        <CircularProgress size={18} sx={{ color: SZ.teal700 }} />
        <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>Pulling market data…</Txt>
      </Flx>
    );
  } else if (error) {
    body = <Callout tone="bad" title="Market data unavailable">{error}</Callout>;
  } else if (!zipcode && !resolved) {
    body = (
      <EmptyState
        title="Add a property ZIP"
        description="Market data resolves per ZIP — HouseCanary first, ZHVI when HouseCanary has no coverage."
      />
    );
  } else if (!source) {
    body = (
      <Callout tone="warn" title="No market data for this ZIP">
        Neither HouseCanary nor ZHVI has coverage here. Refresh to retry.
      </Callout>
    );
  } else {
    body = (
      <>
        {source === "zhvi" && (
          <Txt sx={{ fontSize: 11.5, color: SZ.ink400, mb: 1.5, fontStyle: "italic" }}>
            ZHVI (HouseCanary unavailable for this ZIP)
          </Txt>
        )}
        {source === "housecanary" ? (
          <HouseCanaryBody data={resolved.data} />
        ) : (
          <ZhviBody data={resolved.data} />
        )}
        <AssessmentBody assessment={marketAssessment} />
      </>
    );
  }

  return (
    <ResultsCard
      title={title}
      pill={pill}
      foot={
        <Flx jb ac g={1}>
          <Txt component="span" sx={{ fontSize: 11.5, color: SZ.ink400 }}>
            HouseCanary pulls are cached 30 days per ZIP.
          </Txt>
          <Box
            component="button"
            type="button"
            onClick={() => load(zipcode || resolved?.zipCode)}
            disabled={(!zipcode && !resolved?.zipCode) || busy}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              border: `1px solid ${SZ.lineStrong}`,
              background: SZ.card,
              color: SZ.teal700,
              borderRadius: "6px",
              padding: "3px 9px",
              fontSize: 11.5,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: busy ? "default" : "pointer",
              opacity: (!zipcode && !resolved?.zipCode) || busy ? 0.5 : 1,
              "& svg": { fontSize: 13 },
            }}
          >
            <RefreshRoundedIcon />
            Refresh
          </Box>
        </Flx>
      }
    >
      {body}
    </ResultsCard>
  );
}
